import React, { useRef, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/mainviewcatsslider.css';

const MainViewCatsSlider = () => {
    const { t, language } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const sliderRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [showTip, setShowTip] = useState(true);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hasMoved, setHasMoved] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/categories`);
            // Filter only active categories and sort by display_order
            const activeCategories = response.data
                .filter(cat => cat.is_active)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            setCategories(activeCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // Transform categories for display with translations
    const cats = useMemo(() => {
        return categories.map(cat => ({
            id: cat.id,
            name: language === 'ar' ? cat.name_ar : cat.name_en,
            key: cat.key_name,
            image: cat.image ? getUploadUrl(cat.image) : null,
            link: `/${cat.key_name}`,
            width: '180px' // Default width, can be adjusted
        }));
    }, [categories, language]);

    // Initialize scroll position to right (RTL)
    useEffect(() => {
        if (sliderRef.current) {
            const slider = sliderRef.current;
            slider.scrollLeft = 0;
        }
    }, [cats]);

    const handleMouseDown = (e) => {
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        setIsDragging(true);
        setHasMoved(false);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
        sliderRef.current.style.cursor = 'grabbing';
        sliderRef.current.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        if (sliderRef.current) {
            sliderRef.current.style.cursor = 'grab';
            sliderRef.current.style.userSelect = 'auto';
        }
    };

    const handleMouseUp = (e) => {
        if (hasMoved && e) {
            e.preventDefault();
        }
        setIsDragging(false);
        setHasMoved(false);
        if (sliderRef.current) {
            sliderRef.current.style.cursor = 'grab';
            sliderRef.current.style.userSelect = 'auto';
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        
        if (Math.abs(walk) > 5) {
            setHasMoved(true);
        }
        
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchStart = (e) => {
        if (!hasInteracted) {
            setHasInteracted(true);
            setShowTip(false);
        }
        setIsDragging(true);
        setHasMoved(false);
        setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        
        if (Math.abs(walk) > 5) {
            setHasMoved(true);
        }
        
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = (e) => {
        if (hasMoved && e) {
            e.preventDefault();
        }
        setIsDragging(false);
        setHasMoved(false);
    };

    if (loading) {
        return (
            <div className='main-view-cats-slider'>
                <div className='container-fluid'>
                    <div className='loading-message'>
                        <p>{t('categories.loading') || 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='main-view-cats-slider'>
            <div className='container-fluid'>
                <div className='main-view-cats-slider-wrapper'>
                    <div 
                        className="slider"
                        ref={sliderRef}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleMouseLeave}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {cats.map((cat) => (
                            <div 
                                className='slide' 
                                key={cat.id}
                                style={{ width: cat.width }}
                            >
                                <Link 
                                    to={cat.link} 
                                    onClick={(e) => {
                                        if (hasMoved || isDragging) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="slider-link"
                                >
                                    <div className='cats-slider-item'>
                                        {cat.image ? (
                                            <img src={cat.image} alt={cat.name} className='cat-image' />
                                        ) : (
                                            <div className='cat-image-placeholder'>{cat.name}</div>
                                        )}
                                        <h3>{cat.name}</h3>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                    
                </div>
            </div>
        </div>
    );
};

export default MainViewCatsSlider;
